import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import Points from 'components/Points';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { EmojiPicker } from 'components/EmojiPicker/EmojiPicker';
import { useSendMessageMutation } from 'store/services/message/messageApi';
import { useGetTradeCaseQuery } from 'store/services/case/caseApi';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { AssetUrls } from 'config/assets';
import type { CaseSource } from 'types/interfaces/thread';
import { STATUS_TONE_CLASS } from 'screens/Main/tabs/Inbox/threadTypeStyles';
import { SummonAdminAction } from 'screens/Main/tabs/Inbox/components/SummonAdminAction';
import { SummonAdminWizard } from 'components/SummonAdminWizard/SummonAdminWizard';
import { formatCaseNumber } from 'utils/caseNumber';
import {
  ACCEPTED_IMAGE_ACCEPT_ATTR,
  MAX_IMAGES_PER_MESSAGE,
  validateImageFiles,
} from 'constants/messageImages';
import { cardCountOf, shipLine, type ContactTradeSummary } from './contactTradeSummary';
import 'styles/caseStatusBadges.css';
import 'styles/caseSummoned.css';
import './ContactUserModal.css';

export type ContactAbout = 'TRADE' | 'DIRECT';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  receiverId: number;
  receiverUsername: string;
  receiverAvatar?: string;
  /**
   * The trade this modal was opened from (Sending and Receiving pages). With
   * it the modal offers the About choice and posts into the trade's thread by
   * default (Asana 1219952262403623, Decision 15); without it, as on a
   * profile, the message goes to the direct thread as before.
   */
  tradeSummary?: ContactTradeSummary;
  /** Page the modal was opened from; recorded on a summoned case so admins see where it came from. */
  source?: CaseSource;
}

export const ContactUserModal: React.FC<Props> = ({
  isOpen,
  onClose,
  receiverId,
  receiverUsername,
  receiverAvatar,
  tradeSummary,
  source = 'INBOX',
}) => {
  const tradeId = tradeSummary?.tradeId;
  const [message, setMessage] = useState('');
  const [about, setAbout] = useState<ContactAbout>('TRADE');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [hasJustSummoned, setHasJustSummoned] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [sendMessage, { isLoading }] = useSendMessageMutation();

  // Check if case exists for this trade (only if tradeId provided)
  const { data: caseData, refetch: refetchCase } = useGetTradeCaseQuery(tradeId!, {
    skip: !isOpen || !tradeId
  });

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setMessage('');
      setAbout('TRADE');
      setShowEmojiPicker(false);
      setHasJustSummoned(false);
      setWizardOpen(false);
      // Cleanup image preview URLs
      imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
      setPendingImages([]);
      setImagePreviewUrls([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleEmojiSelect = (emoji: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMessage = message.substring(0, start) + emoji + message.substring(end);

    setMessage(newMessage);

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  const addFiles = useCallback((files: File[]) => {
    if (files.length === 0) return;

    const remainingSlots = MAX_IMAGES_PER_MESSAGE - pendingImages.length;
    if (remainingSlots <= 0) {
      toast.warning(`Maximum ${MAX_IMAGES_PER_MESSAGE} images per message`);
      return;
    }

    const { accepted, errors, capHit } = validateImageFiles(files, remainingSlots);
    errors.forEach((text) => toast.error(text));
    if (capHit) toast.warning(`Only ${remainingSlots} more image(s) can be added`);

    if (accepted.length > 0) {
      setPendingImages(prev => [...prev, ...accepted]);
      setImagePreviewUrls(prev => [...prev, ...accepted.map((file) => URL.createObjectURL(file))]);
    }
  }, [pendingImages.length]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = '';
  }, [addFiles]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageFiles = items
      .filter(item => item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter((f): f is File => f !== null);

    if (imageFiles.length > 0) {
      e.preventDefault();
      addFiles(imageFiles);
    }
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleRemoveImage = useCallback((index: number) => {
    URL.revokeObjectURL(imagePreviewUrls[index]);
    setPendingImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  }, [imagePreviewUrls]);

  const postsToTrade = !!tradeSummary && about === 'TRADE';

  const handleSend = async () => {
    if ((!message.trim() && pendingImages.length === 0) || message.length > 2000) return;

    try {
      await sendMessage({
        receiverId,
        content: message.trim(),
        ...(pendingImages.length > 0 ? { images: pendingImages } : {}),
        // Without a trade the body stays as it always was; the backend resolves the direct thread.
        ...(tradeSummary
          ? postsToTrade
            ? { subjectType: 'TRADE' as const, subjectId: tradeSummary.tradeId }
            : { subjectType: 'DIRECT' as const }
          : {}),
      }).unwrap();

      toast.success(`Message sent to ${receiverUsername}!`);
      onClose();
    } catch (error: any) {
      console.error('Failed to send message:', error);
      // 429 errors are now handled globally in baseQuery
      // Only handle other errors here
      if (error.status !== 429) {
        toast.error(error.data?.message || 'Failed to send message. Please try again.');
      }
    }
  };

  // "Summon Admin…" opens the wizard (Decision 15); the wizard posts the case.
  const handleSummoned = () => {
    setHasJustSummoned(true);
    refetchCase();
  };

  if (!isOpen) return null;

  const caseExists = !!caseData?.exists || hasJustSummoned;
  const caseStatus = caseData?.status ?? caseData?.case?.status;
  const caseNumber = formatCaseNumber(caseData?.caseNumber);

  const modalContent = (
    <div className="contact-modal-overlay" onClick={onClose}>
      <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
        <div className="contact-modal-header">
          <h2 className="contact-modal-title">Contact {receiverUsername}</h2>
          <button className="contact-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="contact-modal-content">
          <div className="contact-user-info">
            <AvatarWithFlag
              avatarUrl={receiverAvatar}
              username={receiverUsername}
              size={40}
            />
            <span className="contact-username">{receiverUsername}</span>
          </div>

          {tradeSummary && (
            <fieldset className="contact-about" data-testid="contact-about">
              <legend className="contact-label">About</legend>
              <label className={`contact-about-option${about === 'TRADE' ? ' selected' : ''}`}>
                <input
                  type="radio"
                  name="contact-about"
                  value="TRADE"
                  checked={about === 'TRADE'}
                  onChange={() => setAbout('TRADE')}
                />
                <span className="contact-about-card">
                  <span className="contact-about-card-top">
                    <span className="contact-about-trade">Trade {tradeSummary.tradeNumber}</span>
                    <span className={`status-badge contact-about-status ${STATUS_TONE_CLASS[tradeSummary.statusTone]}`}>
                      {tradeSummary.statusLabel}
                    </span>
                  </span>
                  <span className="contact-about-cards">
                    {tradeSummary.items[0]?.name ?? 'Cards'}
                    {cardCountOf(tradeSummary) > 1 && ` + ${cardCountOf(tradeSummary) - 1} more`}
                  </span>
                  <span className="contact-about-meta">
                    <span>{shipLine(tradeSummary)}</span>
                    <Points points={tradeSummary.totalValue} type="mini" />
                  </span>
                </span>
              </label>
              <label className={`contact-about-option${about === 'DIRECT' ? ' selected' : ''}`}>
                <input
                  type="radio"
                  name="contact-about"
                  value="DIRECT"
                  checked={about === 'DIRECT'}
                  onChange={() => setAbout('DIRECT')}
                />
                <span className="contact-about-direct">
                  Something else <span className="contact-about-direct-sub">· direct message to {receiverUsername}</span>
                </span>
              </label>
            </fieldset>
          )}

          <div
            className={`contact-form-group${isDragging ? ' drop-zone-active' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <label className="contact-label">Message</label>
            <textarea
              ref={textareaRef}
              className="contact-textarea"
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onPaste={handlePaste}
              rows={6}
              maxLength={2000}
            />
            <CharacterCounter
              currentLength={message.length}
              maxLength={2000}
              warningThreshold={1800}
            />
            {tradeSummary && (
              <p className="contact-destination-note" data-testid="contact-destination">
                {postsToTrade
                  ? `Goes to the Trade ${tradeSummary.tradeNumber} thread, so you and ${receiverUsername} see the trade details next to the message.`
                  : `Goes to your Direct thread with ${receiverUsername}, with no trade attached.`}
              </p>
            )}
          </div>

          {imagePreviewUrls.length > 0 && (
            <div className="image-preview-strip">
              {imagePreviewUrls.map((url, index) => (
                <div key={url} className="image-preview-item">
                  <img src={url} alt={`Preview ${index + 1}`} className="image-preview-thumbnail" />
                  <button
                    className="image-preview-remove"
                    onClick={() => handleRemoveImage(index)}
                    type="button"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="contact-actions">
            <div className="emoji-picker-container">
              <button
                ref={emojiButtonRef}
                className="emoji-picker-button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                type="button"
              >
                😀
              </button>
              {showEmojiPicker && (
                <EmojiPicker
                  onEmojiSelect={handleEmojiSelect}
                  onClose={() => setShowEmojiPicker(false)}
                  buttonRef={emojiButtonRef}
                />
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_ACCEPT_ATTR}
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <button
              className="image-attach-button"
              onClick={() => fileInputRef.current?.click()}
              type="button"
              title="Attach images"
              disabled={pendingImages.length >= MAX_IMAGES_PER_MESSAGE}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </button>

            {tradeId && !caseExists && (
              <SummonAdminAction
                caseState={null}
                onSummon={() => setWizardOpen(true)}
                layout="inline"
              />
            )}

            <MagicButton
              label={isLoading ? 'Sending...' : 'Send Message'}
              color="purple"
              onPress={handleSend}
              isDisabled={
                (!message.trim() && pendingImages.length === 0) ||
                message.length > 2000 ||
                isLoading
              }
            />
          </div>

          {tradeId && caseExists && (
            <div className="contact-summon-admin-section">
              <div className="case-summoned-status">
                <img
                  src={AssetUrls.icons.navigation.adminCases}
                  alt="Admin Summoned"
                  style={{ width: 20, height: 20 }}
                />
                <div>
                  <p className="summoned-text">
                    {caseNumber ? `Case ${caseNumber} open` : 'Admin Summoned'}
                    {caseStatus ? ` · ${caseStatus.replace('_', ' ').toLowerCase()}` : ''}
                  </p>
                  <p className="summoned-details">
                    {caseData?.summonedBy && `Case created by ${caseData.summonedBy}`}
                    {caseData?.alsoRequestedBy && ` and ${caseData.alsoRequestedBy}`}
                    {hasJustSummoned && !caseData?.exists && 'An admin will review shortly'}
                    {caseData?.conversationId && (
                      <>
                        {' · '}
                        <Link to={`/inbox/${caseData.conversationId}`} onClick={onClose}>View trade thread</Link>
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      {tradeSummary && (
        <SummonAdminWizard
          isOpen={wizardOpen}
          onClose={() => setWizardOpen(false)}
          trade={tradeSummary}
          source={source}
          onSummoned={handleSummoned}
        />
      )}
    </>
  );
};
